/**
 * Focus trapping matches `assets/header.js` HeaderBar (#trapFocus, #installKeyHandler pattern):
 * `keydown` includes a branch when `document.activeElement` is not inside the trapped list
 * (including `<body>`) — this must run even when `inert()` is supported.
 */
const POPUP_MODAL_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/*
 * Shopify's cookie consent banner and its preferences modal are injected as
 * siblings in <body>. They must stay interactive while a modal is open so a
 * visitor can always give or (equally importantly) refuse consent — leaving
 * them `inert` would impair the ability to make a freely given choice.
 */
const CONSENT_SURFACE_SELECTOR = '[id^="shopify-pc__"], [class*="shopify-pc__"]';

const isPopupModalFocusableVisible = (element) => {
  if (element.hidden) {
    return false;
  }

  if (element.getAttribute('aria-hidden') === 'true') {
    return false;
  }

  return element.offsetParent !== null || element.getClientRects().length > 0;
};

export class PopupModal extends HTMLElement {
  static observedAttributes = ['open'];

  static #openModals = new Set();

  static #openCount = 0;

  static #inertSnapshot = [];

  static #frozenHeader = [];

  #priorFocus = null;

  #keydownBound = null;

  #closeVisibilityTimeout = null;

  #toggleClickBound = null;

  #supportsInert = 'inert' in HTMLElement.prototype;

  static hasOpenModal(exclude = null) {
    for (const modal of PopupModal.#openModals) {
      if (modal === exclude || !modal.isConnected) {
        continue;
      }

      if (modal.getAttribute('open') === 'true') {
        return true;
      }
    }

    return false;
  }

  static isBlockingUiOpen(exclude = null) {
    if (PopupModal.hasOpenModal(exclude)) {
      return true;
    }

    return document.querySelector('.header-menu[data-menu-state="open"]') !== null;
  }

  static #notifyStackChange() {
    document.dispatchEvent(new CustomEvent('modal-stack:change', {
      detail: { hasOpenModal: PopupModal.#openModals.size > 0 },
    }));
  }

  constructor() {
    super();

    this.#keydownBound = this.#onDocumentKeydown.bind(this);
    this.#toggleClickBound = this.#onToggleClick.bind(this);

    this.style.visibility = 'hidden';

    this.elSection = this.closest('.shopify-section');
    this.elSection?.addEventListener('click', this.#toggleClickBound);

    this.elCloseTriggers = this.querySelectorAll('[data-close]');
    this.elCloseTriggers.forEach(el => el.addEventListener('click', this.close.bind(this)));

    this.preventEditor = this.getAttribute('prevent-editor') === 'true';

    if (this.hasAttribute('data-shopify-editor-block')) {
      document.addEventListener('shopify:block:select', ({ detail }) => {
        const blockData = JSON.parse(this.getAttribute('data-shopify-editor-block'));
        const { id } = blockData;
        id == detail.blockId ? this.setAttribute('open', true) : this.close();
      });
    } else if (!this.preventEditor) {
      document.addEventListener('shopify:section:select', ({ detail }) => {
        const sectionId = `shopify-section-${detail.sectionId}`;
        this.elSection && this.elSection.id == sectionId ? this.setAttribute('open', true) : this.close();
      });
    }
  }

  connectedCallback() {
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'dialog');
    }
  }

  disconnectedCallback() {
    this.elSection?.removeEventListener('click', this.#toggleClickBound);
    document.removeEventListener('keydown', this.#keydownBound);
  }

  #onToggleClick(event) {
    const elToggle = event.target.closest(`[data-toggle][aria-controls="${CSS.escape(this.id)}"]`);

    if (!elToggle) {
      return;
    }

    this.setAttribute('open', this.getAttribute('open') === 'true' ? 'false' : 'true');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name != 'open') {
      return;
    }

    if (newValue == 'true') {
      if (this.#closeVisibilityTimeout !== null) {
        window.clearTimeout(this.#closeVisibilityTimeout);
        this.#closeVisibilityTimeout = null;
      }

      PopupModal.#openModals.add(this);
      PopupModal.#notifyStackChange();

      this.#priorFocus =
        document.activeElement instanceof HTMLElement &&
          typeof document.activeElement.focus === 'function' &&
          !this.contains(document.activeElement)
          ? document.activeElement
          : null;

      this.open = true;
      this.style.visibility = 'visible';
      this.ariaHidden = false;
      this.setAttribute('aria-modal', 'true');
      this.tabIndex = 0;

      this.#suspendRestOfPage();

      /*
       * Same as HeaderBar `#installKeyHandler`: bubbling listener on document
       * (not capture-only Tab when `inert` is missing).
       */
      document.addEventListener('keydown', this.#keydownBound);

      this.#focusFirstElement();
    } else {
      this.open = false;

      PopupModal.#openModals.delete(this);
      PopupModal.#notifyStackChange();

      document.removeEventListener('keydown', this.#keydownBound);
      this.#resumeRestOfPage();

      this.removeAttribute('aria-modal');

      const toRestore = this.#priorFocus;
      this.#priorFocus = null;
      if (toRestore && typeof toRestore.focus === 'function') {
        toRestore.focus({ preventScroll: true });
      }

      if (this.#closeVisibilityTimeout !== null) {
        window.clearTimeout(this.#closeVisibilityTimeout);
      }

      this.#closeVisibilityTimeout = window.setTimeout(() => {
        this.#closeVisibilityTimeout = null;
        this.style.visibility = 'hidden';
        this.ariaHidden = true;
        this.tabIndex = -1;
      }, 300);
    }
  }

  close() {
    this.setAttribute('open', false);
  }

  #focusFirstElement() {
    const focusables = this.#getTrapFocusables();
    const target = focusables[0] ?? this;
    target?.focus?.({ preventScroll: true });
  }

  #trapFocus(event) {
    const focusables = this.#getTrapFocusables();

    if (focusables.length === 0) {
      event.preventDefault();

      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    } else if (!focusables.includes(active)) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  #getTrapFocusables() {
    const candidates = this.querySelectorAll(POPUP_MODAL_FOCUSABLE_SELECTOR);

    return Array.from(candidates).filter(isPopupModalFocusableVisible);
  }

  #onDocumentKeydown(event) {
    if (this.getAttribute('open') !== 'true') {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();

      return;
    }

    if (event.key === 'Tab') {
      this.#trapFocus(event);
    }
  }

  #suspendRestOfPage() {
    PopupModal.#openCount += 1;

    if (PopupModal.#openCount > 1) {
      return;
    }

    /*
     * Lock the viewport scroller only — never `position: fixed` on body,
     * which jumps the page by the promo bar height and needs a `scrollTo`
     * restore on close. Freeze the sticky header first: `overflow: hidden`
     * on `html` unsticks `header-bar` inside the absolutely positioned
     * header overlay.
     */
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    PopupModal.#freezeHeader();
    document.documentElement.classList.add('overflow-hidden');

    if (window.scrollX !== scrollX || window.scrollY !== scrollY) {
      window.scrollTo({ top: scrollY, left: scrollX, behavior: 'instant' });
    }

    if (!this.#supportsInert || !document.body) {
      return;
    }

    const roots = [];

    for (const child of document.body.children) {
      if (!(child instanceof HTMLElement)) {
        continue;
      }

      if (child.contains(this)) {
        continue;
      }

      if (child.matches(CONSENT_SURFACE_SELECTOR)) {
        continue;
      }

      roots.push(child);
    }

    PopupModal.#inertSnapshot = roots.map((el) => ({
      element: el,
      previousInert: el.inert,
    }));

    for (const { element } of PopupModal.#inertSnapshot) {
      element.inert = true;
    }
  }

  #resumeRestOfPage() {
    PopupModal.#openCount = Math.max(0, PopupModal.#openCount - 1);

    if (PopupModal.#openCount > 0) {
      return;
    }

    document.documentElement.classList.remove('overflow-hidden');
    PopupModal.#unfreezeHeader();

    if (!this.#supportsInert) {
      PopupModal.#inertSnapshot = [];

      return;
    }

    for (const { element, previousInert } of PopupModal.#inertSnapshot) {
      element.inert = previousInert;
    }

    PopupModal.#inertSnapshot = [];
  }

  static #freezeHeader() {
    const pending = [];

    for (const el of [
      document.querySelector('promo-bar, [data-promo-banner], #FreeShippingBanner'),
      document.querySelector('header-bar'),
    ]) {
      if (!(el instanceof HTMLElement)) {
        continue;
      }

      const rect = el.getBoundingClientRect();

      if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
        continue;
      }

      pending.push({
        el,
        rect,
        cssText: el.style.cssText,
      });
    }

    PopupModal.#frozenHeader = pending.map(({ el, cssText }) => ({ el, cssText }));

    for (const { el, rect } of pending) {
      el.style.position = 'fixed';
      el.style.top = `${Math.round(rect.top)}px`;
      el.style.left = `${Math.round(rect.left)}px`;
      el.style.width = `${Math.round(rect.width)}px`;
      el.style.right = 'auto';
      el.style.marginTop = '0px';
    }
  }

  static #unfreezeHeader() {
    for (const { el, cssText } of PopupModal.#frozenHeader) {
      el.style.cssText = cssText;
    }

    PopupModal.#frozenHeader = [];
  }
}

if (!customElements.get('popup-modal')) {
  customElements.define('popup-modal', PopupModal);
}

PopupModal.CONSENT_SURFACE_SELECTOR = CONSENT_SURFACE_SELECTOR;
window.PopupModal = PopupModal;
